import type { EnvCheckResult } from "@/lib/env-check";
import { AlertTriangle } from "lucide-react";

/**
 * Full-screen fallback rendered when a startup env check fails. Uses raw
 * Tailwind classes only (no shadcn / providers) so it works even if the app
 * can't be initialized. Do not add data fetching or context reads here.
 */
const EnvCheckError = ({ result }: { result: EnvCheckResult }) => {
  const errors = result.issues.filter((i) => i.severity === "error");
  const warnings = result.issues.filter((i) => i.severity === "warning");

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl space-y-6">
        <div className="flex items-start gap-3">
          <div className="mt-1 h-10 w-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-none">
            <AlertTriangle size={20} aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Backend configuration error</h1>
            <p className="text-sm text-muted-foreground mt-1">
              The app can't start because one or more required <code>VITE_SUPABASE_*</code>{" "}
              environment variables are missing or don't match this project. Fix the values below
              and reload.
            </p>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-3">
            <p className="text-sm font-medium text-destructive">
              {errors.length} error{errors.length === 1 ? "" : "s"} must be fixed
            </p>
            <ul className="space-y-2 text-sm">
              {errors.map((issue, i) => (
                <li key={`err-${i}`} className="space-y-1">
                  <p className="font-mono text-xs text-destructive">{issue.variable}</p>
                  <p className="text-foreground">{issue.message}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
            <p className="text-sm font-medium">
              {warnings.length} warning{warnings.length === 1 ? "" : "s"}
            </p>
            <ul className="space-y-2 text-sm">
              {warnings.map((issue, i) => (
                <li key={`warn-${i}`} className="space-y-1">
                  <p className="font-mono text-xs text-muted-foreground">{issue.variable}</p>
                  <p className="text-foreground">{issue.message}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-lg border border-border bg-card p-4 space-y-2 text-sm">
          <p className="font-medium">How to fix</p>
          <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
            <li>
              Reconnect the project's backend so <code>.env</code> is regenerated with the correct{" "}
              <code>VITE_SUPABASE_URL</code>, <code>VITE_SUPABASE_PUBLISHABLE_KEY</code>, and{" "}
              <code>VITE_SUPABASE_PROJECT_ID</code>.
            </li>
            <li>
              All three values must reference the same project ref (the subdomain of the URL, the{" "}
              <code>ref</code> claim in the JWT, and the project id).
            </li>
            <li>Redeploy or reload the preview after the values are updated.</li>
          </ol>
        </div>

        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none">Diagnostic snapshot</summary>
          <pre className="mt-2 whitespace-pre-wrap break-all rounded bg-muted p-3 font-mono text-[11px]">
{`VITE_SUPABASE_URL           = ${result.values.url ?? "(unset)"}
VITE_SUPABASE_PROJECT_ID    = ${result.values.projectId ?? "(unset)"}
VITE_SUPABASE_PUBLISHABLE_KEY = ${
              result.values.publishableKey
                ? `${result.values.publishableKey.slice(0, 24)}… (${result.values.publishableKey.length} chars)`
                : "(unset)"
            }
URL project ref             = ${result.values.urlRef ?? "(unknown)"}
Key project ref (JWT.ref)   = ${result.values.keyRef ?? "(unknown)"}`}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default EnvCheckError;
